import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SafetyReportFormData, SafetyReportFormVersionData, SafetyReportItemData, SafetyReportItemListing, SafetyReportData, SafetyReportItemListingData, NoteData, NoteSaveData, NoteDeleteData, ActionItemSaveData, ActionItemNoteSaveData, SafetyReportStatus, StudentActionItemData, SafetyReportFormTemplateData, SafetyReportRiskItems, ExportData } from './safety-report-types';

export interface SaveRiskItemsPayload {
  id?: number;
  extreme_message: string;
  high_message: string;
  low_message:string;
  medium_message: string;
  moderate_message: string;
  likelihood: string;
  severity: string;
  matrix: {
    likelihoodItems: string[];
    severityItems: string[];
    scores: number[][];
  };
}

@Injectable()
export class SafetyReportsService {

  readonly saveReportFormUrl: string = '/safetyReport/saveForm.json';
  readonly saveReportUrl: string = '/safetyReport/saveReport.json';
  readonly saveReportNoteUrl: string = '/safetyReport/saveReportNote.json';
  readonly saveActionItemUrl: string = '/safetyReport/saveActionItem.json';
  readonly saveActionItemNoteUrl: string = '/safetyReport/saveActionItemNote.json';
  readonly saveReportStatusUrl: string = '/safetyReport/saveReportStatus.json';
  readonly saveReportEnableNotesUrl: string = '/safetyReport/saveReportEnableNotes.json';
  readonly saveRiskAssessmentURL: string = '/safetyReport/saveRiskAssessment.json';
  readonly saveRiskItemsUrl: string = '/safetyReport/saveRiskItems.json';
  readonly getRiskItemsUrl: string = '/safetyReport/getRiskItems.json';
  readonly deleteReportNoteUrl: string = '/safetyReport/deleteReportNote.json';

  readonly toggleActionItemStatusUrl: string = '/safetyReport/toggleActionItemStatus.json';

  readonly getReportFormsUrl: string = '/safetyReport/getForms.json';
  readonly getReportFormUrl: string = '/safetyReport/getForm.json';
  readonly getReportsUrl: string = '/safetyReport/getReports.json';
  readonly getReportUrl: string = '/safetyReport/getReport.json';

  readonly getActionItemsUrl: string = '/safetyReport/getActionItems.json';
  readonly getReportFormTemplatesUrl: string = '/safetyReport/getReportFormTemplates.json';
  readonly getReportExportDataUrl: string = '/safetyReport/getReportExportData.json';

  constructor(private http: HttpClient) { }

  saveReportForm(formData: SafetyReportFormData) {
    return this.http.post<SafetyReportFormData>(this.saveReportFormUrl, formData);
  }

  saveReport(report: SafetyReportData) {
    return this.http.post<SafetyReportItemListingData>(this.saveReportUrl, report);
  }

  saveReportNote(note: NoteSaveData) {
    return this.http.post<NoteData>(this.saveReportNoteUrl, note);
  }

  saveActionItem(actionItem: ActionItemSaveData) {
    return this.http.post<boolean>(this.saveActionItemUrl, actionItem);
  }

  saveActionItemNote(note: ActionItemNoteSaveData) {
    return this.http.post<boolean>(this.saveActionItemNoteUrl, note);
  }

  saveReportStatus(reportId: number, status: SafetyReportStatus, comments: string = '', notifyGroup: number = -1, deleteNotes: boolean = false) {
    return this.http.post<boolean>(this.saveReportStatusUrl, {reportId: reportId, status: status, comments: comments, notifyGroup: notifyGroup, deleteNotes: deleteNotes});
  }

  saveReportEnableNotes(reportId: number) {
    return this.http.post<boolean>(this.saveReportEnableNotesUrl, {reportId: reportId});
  }

  saveRiskAssessment(reportId: number, risk) {
    return this.http.post<boolean>(this.saveRiskAssessmentURL, {reportId: reportId, risk});
  }

  saveRiskItems(payload: SaveRiskItemsPayload) {
    return this.http.post<boolean>(this.saveRiskItemsUrl, payload);
  }

  getRiskItems() {
    return this.http.get<(SafetyReportRiskItems & { matrix?: SaveRiskItemsPayload['matrix'] }) | (SafetyReportRiskItems & { matrix?: SaveRiskItemsPayload['matrix'] })[]>(this.getRiskItemsUrl);
  }

  toggleActionItemStatus(reportId: number, actionItemId: string, actionItemStatus: number) {
    return this.http.post<boolean>(this.toggleActionItemStatusUrl, {reportId: reportId, actionItemId: actionItemId, actionItemStatus: actionItemStatus});
  }

  getReportForms() {
    return this.http.get<SafetyReportFormData[]>(this.getReportFormsUrl);
  }

  getReportForm(safetyReportFormId: number) {
    return this.http.post<SafetyReportFormVersionData>(this.getReportFormUrl, {id: safetyReportFormId});
  }

  getReports() {
    return this.http.get<SafetyReportItemListing[]>(this.getReportsUrl);
  }

  getActionItems() {
    return this.http.get<StudentActionItemData[]>(this.getActionItemsUrl);
  }

  getReport(safetyReportId: number) {
    return this.http.post<SafetyReportItemData>(this.getReportUrl, {id: safetyReportId});
  }

  getReportFormTemplates() {
    return this.http.get<SafetyReportFormTemplateData[]>(this.getReportFormTemplatesUrl);
  }

  getReportExportData(safetyReportFormId: number) {
    return this.http.post<ExportData>(this.getReportExportDataUrl, {reportFormId: safetyReportFormId});
  }

  deleteReportNote(noteData: NoteDeleteData) {
    return this.http.post<NoteData>(this.deleteReportNoteUrl, noteData);
  }

}

let safteyReportsServiceStub: Partial<SafetyReportsService> = {};
export { safteyReportsServiceStub }
